// Lazy Stimulus controller registration.
//
// Heavy controllers (the TipTap rich-text editor, OpenLayers maps) pull in
// hundreds of KB of dependencies. Registering them eagerly would bundle all of
// that into the every-page application.js. Instead, the auto-generated
// controllers/index.js hands those identifiers here and we register each one
// only once an element using it appears in the DOM — so esbuild's code-split
// chunk is fetched on the pages that actually need it.
//
// The lazy set is defined by LAZY_CONTROLLER_TAGS in
// lib/tasks/stimulus_tasks.rake; this helper is the runtime side.
//
//   registerLazyControllers(application, {
//     "tiptap-input": () => import("../../view_components/ui/form/tiptap_input_controller"),
//   })
export function registerLazyControllers(application, loaders) {
  const identifiers = Object.keys(loaders)
  const registered = new Set()
  let observer = null

  const present = (identifier) =>
    document.querySelector(`[data-controller~="${identifier}"]`) !== null

  const loadPresent = () => {
    for (const identifier of identifiers) {
      if (registered.has(identifier) || !present(identifier)) continue

      // Mark before the async import resolves so a burst of mutations can't
      // kick off a second fetch/register for the same identifier.
      registered.add(identifier)
      loaders[identifier]()
        .then((module) => application.register(identifier, module.default))
        .catch((error) => {
          registered.delete(identifier)
          console.error(`Failed to lazy-load Stimulus controller "${identifier}"`, error)
        })
    }

    // Stimulus keeps managing a controller across Turbo visits once registered,
    // so once every identifier has loaded there is nothing left to watch for.
    if (registered.size === identifiers.length) {
      observer?.disconnect()
      document.removeEventListener("turbo:load", loadPresent)
      document.removeEventListener("turbo:frame-load", loadPresent)
    }
  }

  document.addEventListener("turbo:load", loadPresent)
  document.addEventListener("turbo:frame-load", loadPresent)

  // MutationObserver is the catch-all: it sees Turbo Stream insertions, frame
  // swaps, and any other dynamic markup that the turbo: events above might miss.
  if (typeof MutationObserver !== "undefined") {
    observer = new MutationObserver(loadPresent)
    observer.observe(document.documentElement, {
      childList: true,
      subtree: true,
      attributeFilter: ["data-controller"],
    })
  }

  loadPresent()
}
